import React, { useContext, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { auth } from '~config/firebase'
import {
    signOut,
    multiFactor,
    User as AuthUser,
    onAuthStateChanged,
} from 'firebase/auth'

import {
    AuthRoles,
    UserInvitationStatus,
    useGenerateIntercomJwtQuery,
    useGetUserQuery,
    useUpdateUserMutation,
    UserFieldsFragment,
} from '~graphql/generated/graphql'
import { UserRoutes } from './routes'
import { LocalStorageKeys } from '~config/constants'
import { shutdown } from '@intercom/messenger-js-sdk'

export const UserContext = React.createContext<UserProviderType>({
    user: undefined,
    isLoading: true,
    userId: '',
    intercomJwt: undefined,
    isAdmin: false,
    isEmailVerified: false,
    isPhoneVerified: false,
    hasMFAEnabled: false,
    signOut: () => {},
    refetchUser: async () => {},
})

type UserProviderType = {
    user: UserFieldsFragment | undefined
    isLoading: boolean
    userId: string
    intercomJwt: string | undefined
    isAdmin: boolean
    isEmailVerified: boolean
    isPhoneVerified: boolean
    hasMFAEnabled: boolean
    signOut: () => void
    refetchUser: () => Promise<void>
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => (
    <UserContext.Provider value={{ ...useUserValues() }}>
        {children}
    </UserContext.Provider>
)

export type LoginStatus = 'error' | 'success' | 'requires-mfa'

function useUserValues(): UserProviderType {
    const [userId, setUserId] = useState(getInitialAuthUserId)
    const [intercomJwt, setIntercomJwt] = useState<string | undefined>()
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false)
    const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false)
    const [hasMFAEnabled, setHasMFAEnabled] = useState<boolean>(false)
    const [{ user, isLoading }, setUser] = useState<{
        user: UserFieldsFragment | undefined
        isLoading: boolean
    }>({
        user: persistedUser,
        // Stay in a loading state until Firebase has resolved the session.
        // Starting as `false` with no user makes every consumer treat the
        // first paint as "signed out" (Access Restricted, empty permissions).
        isLoading: !persistedUser,
    })

    function updateUserId(id: string) {
        persistedUserId = id
        setUserId(id)
    }

    function updateUser(next: {
        user: UserFieldsFragment | undefined
        isLoading: boolean
    }) {
        persistedUser = next.user
        setUser(next)
    }

    function handleSignOut() {
        localStorage.removeItem(LocalStorageKeys.authToken)
        persistedUser = undefined
        persistedUserId = ''
        updateUser({ user: undefined, isLoading: true })
        updateUserId('')
        shutdown()
        signOut(auth).then(() => {
            navigate(UserRoutes.Auth)
            updateUser({ user: undefined, isLoading: false })
            updateUserId('')
        })
    }

    const { refetch: refetchUser } = useGetUserQuery({
        onCompleted: data => {
            updateUser({
                isLoading: false,
                user: data?.user || undefined,
            })
        },
        skip: true,
    })

    const { refetch: refetchIntercomJwt } = useGenerateIntercomJwtQuery({
        onCompleted: data => {
            setIntercomJwt(data.generateIntercomJwt ?? undefined)
        },
        skip: true,
    })

    const [updateUserMutation] = useUpdateUserMutation()

    async function handleRefetch() {
        const { data } = await refetchUser()

        await refetchIntercomJwt()

        updateUser({
            user: data.user || undefined,
            isLoading: false,
        })
    }

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async userAuth => {
            handleUserAuthRetrieved(userAuth)
        })

        return () => {
            unsub()
        }
    }, [])

    const handleUserAuthRetrieved = async (userAuth: AuthUser | null) => {
        if (userAuth) {
            const multiFactorUser = multiFactor(userAuth)
            updateUserId(userAuth.uid)
            setIsEmailVerified(userAuth.emailVerified)
            setIsPhoneVerified(
                multiFactorUser.enrolledFactors?.at(0)?.displayName === 'phone'
                    ? true
                    : false,
            )
            setHasMFAEnabled(
                multiFactorUser.enrolledFactors &&
                    multiFactorUser.enrolledFactors.length > 0,
            )
            const token = await userAuth.getIdToken()
            localStorage.setItem(LocalStorageKeys.authToken, token)
            const { data } = await refetchUser({
                id: userAuth.uid,
            })

            await refetchIntercomJwt()

            // Mark invitation as accepted if user was pending
            if (data.user?.invitation_status === UserInvitationStatus.Pending) {
                await updateUserMutation({
                    variables: {
                        updateUserId: userAuth.uid,
                        data: {
                            invitation_status: UserInvitationStatus.Accepted,
                        },
                    },
                })
            }

            updateUser({
                user: data.user || undefined,
                isLoading: false,
            })
        } else {
            persistedUser = undefined
            persistedUserId = ''
            updateUser({
                user: undefined,
                isLoading: false,
            })
            updateUserId('')
        }
    }

    useEffect(() => {
        if (auth.currentUser?.uid) {
            updateUserId(auth.currentUser.uid)
        }
    }, [])

    return {
        user,
        isLoading,
        userId,
        isEmailVerified,
        isPhoneVerified,
        hasMFAEnabled,
        intercomJwt,
        isAdmin: getIsAdminUser(user),
        signOut: handleSignOut,
        refetchUser: handleRefetch,
    }
}

export const useUserContext = () => {
    return useContext(UserContext)
}

/**
 * Survives UserProvider remounts (Gatsby navigation / wrapRoot re-renders)
 * so we don't flash a signed-out state while Firebase + GraphQL rehydrate.
 */
let persistedUser: UserFieldsFragment | undefined
let persistedUserId = ''

function getInitialAuthUserId(): string {
    if (persistedUserId) return persistedUserId
    if (typeof window === 'undefined') return ''
    return auth.currentUser?.uid ?? ''
}

function getIsAdminUser(user: UserFieldsFragment | undefined): boolean {
    return (
        !!user?.role &&
        [AuthRoles.SuperAdmin, AuthRoles.Admin].includes(user.role)
    )
}
